import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { supabaseService, type PullRequest } from '@/shared/services/supabaseService';
import { LoadingSpinner } from '@/ui/LoadingSpinner';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/ui/table';
import { Badge } from '@/ui/badge';
import { cn } from '@/lib/utils';

interface PullRequestListProps {
  repoId: string;
}

const PullRequestList: React.FC<PullRequestListProps> = ({ repoId }) => {
  const navigate = useNavigate();
  const { data: prs, isLoading, isError, error } = useQuery<PullRequest[], Error>({
    queryKey: ['repositoryPullRequests', repoId],
    queryFn: () => supabaseService.getRepositoryPullRequests(repoId, 100), // Fetching up to 100 PRs
    enabled: !!repoId,
  });

  if (isLoading) {
    return (
        <div className="flex justify-center items-center h-48 border border-neutral-200 dark:border-neutral-800 bg-background">
            <LoadingSpinner />
        </div>
    );
  }

  if (isError) {
    return (
        <div className="p-8 border-2 border-red-500 bg-red-50 dark:bg-red-900/10 text-red-900 dark:text-red-200 font-mono text-sm">
            ERROR: {error.message}
        </div>
    );
  }

  const handlePRClick = (prNumber: number) => {
    navigate(`/repository/${repoId}/pr/${prNumber}`);
  };

  if (!prs || prs.length === 0) {
      return (
          <div className="p-12 text-center border-2 border-dashed border-neutral-300 dark:border-neutral-700">
              <p className="font-mono text-sm font-bold uppercase text-muted-foreground">NO_PULL_REQUESTS_FOUND</p>
          </div>
      );
  }

  return (
    <div className="border border-neutral-950 dark:border-neutral-100 bg-background">
        <Table>
          <TableHeader className="bg-neutral-100 dark:bg-neutral-900">
            <TableRow className="hover:bg-transparent border-b border-neutral-950 dark:border-neutral-100">
              <TableHead className="font-mono text-xs uppercase text-neutral-950 dark:text-neutral-100 font-bold h-10 w-[40%]">Title</TableHead>
              <TableHead className="font-mono text-xs uppercase text-neutral-950 dark:text-neutral-100 font-bold h-10">Author</TableHead>
              <TableHead className="font-mono text-xs uppercase text-neutral-950 dark:text-neutral-100 font-bold h-10">Status</TableHead>
              <TableHead className="font-mono text-xs uppercase text-neutral-950 dark:text-neutral-100 font-bold h-10 text-right">Last Updated</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {prs.map((pr) => (
              <TableRow
                key={pr.id}
                onClick={() => handlePRClick(pr.pr_number)}
                className="cursor-pointer border-b border-neutral-950 dark:border-neutral-100 hover:bg-neutral-50 dark:hover:bg-neutral-900/20 transition-colors last:border-b-0"
              >
                <TableCell className="py-4">
                  <div className="font-mono text-sm font-bold truncate max-w-[300px] md:max-w-[400px]">
                    <span className="text-muted-foreground mr-2">#{pr.pr_number}</span>
                    {pr.title}
                  </div>
                </TableCell>
                <TableCell className="font-mono text-xs text-muted-foreground py-4">
                    {pr.author_username}
                </TableCell>
                <TableCell className="py-4">
                    <Badge
                        variant={pr.state === 'open' ? 'default' : 'secondary'}
                        className={cn(
                            "rounded-none font-mono text-[10px] uppercase px-2",
                            pr.state === 'open' ? "bg-green-600 hover:bg-green-700 text-white" : "bg-neutral-200 text-neutral-700 hover:bg-neutral-300 dark:bg-neutral-800 dark:text-neutral-300"
                        )}
                    >
                        {pr.state}
                    </Badge>
                </TableCell>
                <TableCell className="text-right font-mono text-xs text-muted-foreground py-4">
                    {new Date(pr.updated_at).toLocaleDateString()}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
    </div>
  );
};

export default PullRequestList;
